// #region imports

import { Constants } from '../../models';

// #endregion

/**
 * CookieConsent (vanilla-cookieconsent v3) translation blocks.
 *
 * Two categories only: Necessary (always on — logins, session, and the Intercom
 * support chat, all disclosed in the cookie policy) and Analytics (Google Analytics,
 * the only opt-in category). Keyed by the app's full locale (isoTwoLetterName-cultureCode,
 * matching assets/i18n/app, e.g. 'en-GB', 'zh-CN'); ConsentService resolves full locale
 * → short code → English.

 * The privacy-policy URL is interpolated from Constants.PRIVACY_POLICY_LINK.
 */

const privacyPolicyLink = Constants.PRIVACY_POLICY_LINK;

const enTranslation = {
    consentModal: {
      title: `We value your privacy`,
      description: `We use essential cookies to keep you signed in and provide support chat. These are always on. With your permission, we also use analytics cookies to understand how the site is used. You can accept or reject these.`,
      acceptAllBtn: `Accept all`,
      acceptNecessaryBtn: `Reject all`,
      showPreferencesBtn: `Manage preferences`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Privacy Policy</a>`
    },
    preferencesModal: {
      title: `Manage cookie preferences`,
      acceptAllBtn: `Accept all`,
      acceptNecessaryBtn: `Reject all`,
      savePreferencesBtn: `Save preferences`,
      closeIconLabel: `Close`,
      sections: [
        {
          title: `Your privacy choices`,
          description: `This panel lets you choose which non-essential cookies and tools we may use. See our <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Privacy Policy</a> for more detail.`
        },
        {
          title: `Strictly necessary`,
          description: `These cookies are required for the site to function - for example, security, keeping you signed in, your session, remembering your cookie choice, and the support chat. They cannot be switched off.`,
          linkedCategory: `necessary`
        },
        {
          title: `Analytics`,
          description: `Google Analytics helps us understand how the site is used so we can improve it. No advertising or marketing cookies are used.`,
          linkedCategory: `analytics`
        }
      ]
    }
  };

/**
 * Map of locale/language code -> translation block. English is registered under both
 * 'en' (fallback short code) and 'en-GB' (default locale). Other locales were
 * AI translated.
 */
export const CONSENT_TRANSLATIONS: { [langCode: string]: typeof enTranslation } = {
  en: enTranslation,
  'en-GB': enTranslation,
  'ar': {
    consentModal: {
      title: `نحن نحترم خصوصيتك`,
      description: `نستخدم ملفات تعريف الارتباط الأساسية للحفاظ على تسجيل دخولك وتوفير خدمة الدردشة للدعم. وتظل هذه الملفات نشطة دائمًا. وبموافقتك، نستخدم أيضًا ملفات تعريف الارتباط التحليلية لفهم كيفية استخدام الموقع. يمكنك قبول هذه الملفات أو رفضها.`,
      acceptAllBtn: `قبول الكل`,
      acceptNecessaryBtn: `رفض الكل`,
      showPreferencesBtn: `إدارة التفضيلات`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">سياسة الخصوصية</a>`
    },
    preferencesModal: {
      title: `إدارة تفضيلات ملفات تعريف الارتباط`,
      acceptAllBtn: `قبول الكل`,
      acceptNecessaryBtn: `رفض الكل`,
      savePreferencesBtn: `حفظ التفضيلات`,
      closeIconLabel: `إغلاق`,
      sections: [
        {
          title: `خيارات الخصوصية الخاصة بك`,
          description: `تتيح لك هذه اللوحة اختيار ملفات تعريف الارتباط والأدوات غير الأساسية التي قد نستخدمها. راجع <a href="${privacyPolicyLink}" target="_blank" rel="noopener">سياسة الخصوصية</a> الخاصة بنا لمزيد من التفاصيل.`
        },
        {
          title: `ضرورية للغاية`,
          description: `هذه الملفات ضرورية لتشغيل الموقع - على سبيل المثال، للأغراض الأمنية، وللحفاظ على تسجيل دخولك، ولجلسة التصفح الخاصة بك، ولتذكر اختيارك المتعلق بملفات تعريف الارتباط، ولخدمة دردشة الدعم. ولا يمكن تعطيلها.`,
          linkedCategory: `necessary`
        },
        {
          title: `التحليلات`,
          description: `تساعدنا خدمة «Google Analytics» على فهم كيفية استخدام الموقع حتى نتمكن من تحسينه. ولا يتم استخدام أي ملفات تعريف ارتباط إعلانية أو تسويقية.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'bg': {
    consentModal: {
      title: `Ние ценим правото Ви на неприкосновеност на личния живот`,
      description: `Използваме задължителни бисквитки, за да поддържаме влизането Ви в системата и да Ви предоставим чат за поддръжка. Те са винаги активирани. С Ваше разрешение използваме и аналитични бисквитки, за да разберем как се използва сайтът. Можете да ги приемете или отхвърлите.`,
      acceptAllBtn: `Приемете всички`,
      acceptNecessaryBtn: `Отхвърлете всички`,
      showPreferencesBtn: `Управление на настройките`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Политика за поверителност</a>`
    },
    preferencesModal: {
      title: `Управление на настройките за бисквитките`,
      acceptAllBtn: `Приемете всички`,
      acceptNecessaryBtn: `Отхвърлете всички`,
      savePreferencesBtn: `Запазване на настройките`,
      closeIconLabel: `Затворете`,
      sections: [
        {
          title: `Вашите настройки за поверителност`,
          description: `Този панел Ви позволява да изберете кои несъществени бисквитки и инструменти можем да използваме. За повече подробности вижте нашата <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Политика за поверителност</a>.`
        },
        {
          title: `Абсолютно необходими`,
          description: `Тези бисквитки са необходими за функционирането на сайта - например за сигурността, за да останете вписани, за Вашата сесия, за запаметяване на Вашия избор относно бисквитките и за чата за поддръжка. Те не могат да бъдат деактивирани.`,
          linkedCategory: `necessary`
        },
        {
          title: `Аналитика`,
          description: `Google Analytics ни помага да разберем как се използва сайтът, за да можем да го подобрим. Не се използват рекламни или маркетингови бисквитки.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'de': {
    consentModal: {
      title: `Der Schutz Ihrer Privatsphäre ist uns wichtig`,
      description: `Wir verwenden unbedingt erforderliche Cookies, um Ihre Anmeldung aufrechtzuerhalten und den Support-Chat bereitzustellen. Diese sind stets aktiviert. Mit Ihrer Zustimmung verwenden wir außerdem Analyse-Cookies, um zu verstehen, wie die Website genutzt wird. Sie können diese akzeptieren oder ablehnen.`,
      acceptAllBtn: `Alle akzeptieren`,
      acceptNecessaryBtn: `Alle ablehnen`,
      showPreferencesBtn: `Einstellungen verwalten`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Datenschutzerklärung</a>`
    },
    preferencesModal: {
      title: `Cookie-Einstellungen verwalten`,
      acceptAllBtn: `Alle akzeptieren`,
      acceptNecessaryBtn: `Alle ablehnen`,
      savePreferencesBtn: `Einstellungen speichern`,
      closeIconLabel: `Schließen`,
      sections: [
        {
          title: `Ihre Datenschutzoptionen`,
          description: `Über dieses Fenster können Sie auswählen, welche nicht unbedingt erforderlichen Cookies und Tools wir verwenden dürfen. Weitere Informationen finden Sie in unserer <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Datenschutzerklärung</a>.`
        },
        {
          title: `Unbedingt erforderlich`,
          description: `Diese Cookies sind für das Funktionieren der Website erforderlich - beispielsweise für die Sicherheit, um Ihre Anmeldung aufrechtzuerhalten, Ihre Sitzung zu verwalten, Ihre Cookie-Einstellungen zu speichern und den Support-Chat zu ermöglichen. Sie können nicht deaktiviert werden.`,
          linkedCategory: `necessary`
        },
        {
          title: `Analyse`,
          description: `Google Analytics hilft uns dabei, die Nutzung der Website besser zu verstehen, damit wir sie verbessern können. Es werden keine Werbe- oder Marketing-Cookies verwendet.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'el': {
    consentModal: {
      title: `Σεβόμαστε το απόρρητό σας`,
      description: `Χρησιμοποιούμε απαραίτητα cookies για να διατηρούμε τη σύνδεσή σας και να παρέχουμε συνομιλία υποστήριξης. Αυτά είναι πάντα ενεργοποιημένα. Με τη συγκατάθεσή σας, χρησιμοποιούμε επίσης cookies ανάλυσης για να κατανοήσουμε τον τρόπο χρήσης του ιστότοπου. Μπορείτε να τα αποδεχτείτε ή να τα απορρίψετε.`,
      acceptAllBtn: `Αποδοχή όλων`,
      acceptNecessaryBtn: `Απόρριψη όλων`,
      showPreferencesBtn: `Διαχείριση προτιμήσεων`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Πολιτική απορρήτου</a>`
    },
    preferencesModal: {
      title: `Διαχείριση προτιμήσεων για τα cookies`,
      acceptAllBtn: `Αποδοχή όλων`,
      acceptNecessaryBtn: `Απόρριψη όλων`,
      savePreferencesBtn: `Αποθήκευση προτιμήσεων`,
      closeIconLabel: `Κλείσιμο`,
      sections: [
        {
          title: `Οι επιλογές σας σχετικά με την προστασία των προσωπικών σας δεδομένων`,
          description: `Αυτό το παράθυρο σας επιτρέπει να επιλέξετε ποια μη απαραίτητα cookies και εργαλεία μπορούμε να χρησιμοποιήσουμε. Ανατρέξτε <a href="${privacyPolicyLink}" target="_blank" rel="noopener">στην Πολιτική απορρήτου</a> μας για περισσότερες λεπτομέρειες.`
        },
        {
          title: `Απολύτως απαραίτητα`,
          description: `Αυτά τα cookies είναι απαραίτητα για τη λειτουργία του ιστότοπου - για παράδειγμα, για λόγους ασφάλειας, για να παραμείνετε συνδεδεμένοι, για τη διατήρηση της συνεδρίας σας, για την απομνημόνευση της επιλογής σας σχετικά με τα cookies, καθώς και για τη ζωντανή συνομιλία υποστήριξης. Δεν είναι δυνατό να απενεργοποιηθούν.`,
          linkedCategory: `necessary`
        },
        {
          title: `Αναλυτικά στοιχεία`,
          description: `Το Google Analytics μας βοηθά να κατανοήσουμε πώς χρησιμοποιείται ο ιστότοπος, ώστε να μπορούμε να τον βελτιώσουμε. Δεν χρησιμοποιούνται cookies διαφήμισης ή μάρκετινγκ.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'en-US': {
    consentModal: {
      title: `We value your privacy`,
      description: `We use essential cookies to keep you signed in and provide support chat. These are always on. With your permission, we also use analytics cookies to understand how the site is used. You can accept or reject these.`,
      acceptAllBtn: `Accept all`,
      acceptNecessaryBtn: `Reject all`,
      showPreferencesBtn: `Manage preferences`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Privacy Policy</a>`
    },
    preferencesModal: {
      title: `Manage cookie preferences`,
      acceptAllBtn: `Accept all`,
      acceptNecessaryBtn: `Reject all`,
      savePreferencesBtn: `Save preferences`,
      closeIconLabel: `Close`,
      sections: [
        {
          title: `Your privacy choices`,
          description: `This panel lets you choose which non-essential cookies and tools we may use. See our <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Privacy Policy</a> for more detail.`
        },
        {
          title: `Strictly necessary`,
          description: `These cookies are required for the site to function - for example, security, keeping you signed in, your session, remembering your cookie choice, and the support chat. They cannot be switched off.`,
          linkedCategory: `necessary`
        },
        {
          title: `Analytics`,
          description: `Google Analytics helps us understand how the site is used so we can improve it. No advertising or marketing cookies are used.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'es-ES': {
    consentModal: {
      title: `Valoramos tu privacidad`,
      description: `Utilizamos cookies esenciales para mantenerte conectado y ofrecerte el servicio de chat de asistencia. Estas cookies están siempre activas. Con tu permiso, también utilizamos cookies de análisis para conocer cómo se utiliza la web. Puedes aceptarlas o rechazarlas.`,
      acceptAllBtn: `Aceptar todo`,
      acceptNecessaryBtn: `Rechazar todo`,
      showPreferencesBtn: `Gestionar preferencias`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Política de privacidad</a>`
    },
    preferencesModal: {
      title: `Gestionar las preferencias de cookies`,
      acceptAllBtn: `Aceptar todo`,
      acceptNecessaryBtn: `Rechazar todo`,
      savePreferencesBtn: `Guardar preferencias`,
      closeIconLabel: `Cerrar`,
      sections: [
        {
          title: `Tus opciones de privacidad`,
          description: `Este panel te permite elegir qué cookies y herramientas no esenciales podemos utilizar. Consulta nuestra <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Política de privacidad</a> para obtener más información.`
        },
        {
          title: `Estrictamente necesarias`,
          description: `Estas cookies son necesarias para el funcionamiento de la página web; por ejemplo, para la seguridad, para mantenerte conectado, para tu sesión, para recordar tu elección de cookies y para el chat de asistencia. No se pueden desactivar.`,
          linkedCategory: `necessary`
        },
        {
          title: `Análisis`,
          description: `Google Analytics nos ayuda a comprender cómo se utiliza la página web para poder mejorarla. No se utilizan cookies publicitarias ni de marketing.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'es-MX': {
    consentModal: {
      title: `Valoramos tu privacidad`,
      description: `Utilizamos cookies esenciales para mantenerte conectado y ofrecerte el chat de soporte. Estas siempre están activadas. Con tu permiso, también utilizamos cookies de análisis para comprender cómo se utiliza el sitio. Puedes aceptarlas o rechazarlas.`,
      acceptAllBtn: `Aceptar todo`,
      acceptNecessaryBtn: `Rechazar todo`,
      showPreferencesBtn: `Administrar preferencias`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Política de privacidad</a>`
    },
    preferencesModal: {
      title: `Administrar las preferencias de cookies`,
      acceptAllBtn: `Aceptar todo`,
      acceptNecessaryBtn: `Rechazar todo`,
      savePreferencesBtn: `Guardar preferencias`,
      closeIconLabel: `Cerrar`,
      sections: [
        {
          title: `Tus opciones de privacidad`,
          description: `Este panel te permite elegir qué cookies y herramientas no esenciales podemos utilizar. Consulta nuestra <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Política de privacidad</a> para obtener más detalles.`
        },
        {
          title: `Estrictamente necesarias`,
          description: `Estas cookies son necesarias para que el sitio funcione; por ejemplo, para la seguridad, para mantenerte conectado, para tu sesión, para recordar tu elección de cookies y para el chat de soporte. No se pueden desactivar.`,
          linkedCategory: `necessary`
        },
        {
          title: `Análisis`,
          description: `Google Analytics nos ayuda a entender cómo se usa el sitio para poder mejorarlo. No se utilizan cookies de publicidad ni de marketing.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'fr-CA': {
    consentModal: {
      title: `Nous accordons une grande importance à la protection de votre vie privée`,
      description: `Nous utilisons des témoins indispensables pour vous permettre de rester connecté et vous proposer un service d'assistance par clavardage. Ceux-ci sont toujours activés. Avec votre accord, nous utilisons également des témoins d'analyse pour comprendre comment le site est utilisé. Vous pouvez les accepter ou les refuser.`,
      acceptAllBtn: `Tout accepter`,
      acceptNecessaryBtn: `Tout refuser`,
      showPreferencesBtn: `Gérer les préférences`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Politique de confidentialité</a>`
    },
    preferencesModal: {
      title: `Gérer les préférences en matière de témoins`,
      acceptAllBtn: `Tout accepter`,
      acceptNecessaryBtn: `Tout refuser`,
      savePreferencesBtn: `Enregistrer les préférences`,
      closeIconLabel: `Fermer`,
      sections: [
        {
          title: `Vos choix en matière de confidentialité`,
          description: `Ce panneau vous permet de choisir les témoins et outils non essentiels que nous sommes susceptibles d'utiliser. Pour plus d'informations, consultez notre <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Politique de confidentialité</a>.`
        },
        {
          title: `Strictement nécessaires`,
          description: `Ces témoins sont indispensables au bon fonctionnement du site - notamment pour la sécurité, le maintien de votre connexion, la gestion de votre session, la mémorisation de vos préférences en matière de témoins et le service d'assistance par clavardage. Ils ne peuvent pas être désactivés.`,
          linkedCategory: `necessary`
        },
        {
          title: `Analyse`,
          description: `Google Analytics nous aide à comprendre comment le site est utilisé afin que nous puissions l'améliorer. Aucun témoin publicitaire ou marketing n'est utilisé.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'fr-FR': {
    consentModal: {
      title: `Nous accordons une grande importance à la protection de votre vie privée`,
      description: `Nous utilisons des cookies indispensables pour vous permettre de rester connecté et vous proposer un service d'assistance par chat. Ceux-ci sont toujours activés. Avec votre accord, nous utilisons également des cookies d'analyse pour comprendre comment le site est utilisé. Vous pouvez les accepter ou les refuser.`,
      acceptAllBtn: `Tout accepter`,
      acceptNecessaryBtn: `Tout refuser`,
      showPreferencesBtn: `Gérer les préférences`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Politique de confidentialité</a>`
    },
    preferencesModal: {
      title: `Gérer les préférences en matière de cookies`,
      acceptAllBtn: `Tout accepter`,
      acceptNecessaryBtn: `Tout refuser`,
      savePreferencesBtn: `Enregistrer les préférences`,
      closeIconLabel: `Fermer`,
      sections: [
        {
          title: `Vos choix en matière de confidentialité`,
          description: `Ce panneau vous permet de choisir les cookies et outils non essentiels que nous sommes susceptibles d'utiliser. Pour plus d'informations, consultez notre <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Politique de confidentialité</a>.`
        },
        {
          title: `Strictement nécessaires`,
          description: `Ces cookies sont indispensables au bon fonctionnement du site - notamment pour la sécurité, le maintien de votre connexion, la gestion de votre session, la mémorisation de vos préférences en matière de cookies et le service d'assistance par chat. Ils ne peuvent pas être désactivés.`,
          linkedCategory: `necessary`
        },
        {
          title: `Analyse`,
          description: `Google Analytics nous aide à comprendre comment le site est utilisé afin que nous puissions l'améliorer. Aucun cookie publicitaire ou marketing n'est utilisé.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'he': {
    consentModal: {
      title: `אנו מכבדים את פרטיותך`,
      description: `אנו משתמשים בעוגיות חיוניות כדי לשמור על מצב התחברותך ולספק שירות צ'אט לתמיכה. עוגיות אלו פועלות תמיד. באישורך, אנו משתמשים גם בעוגיות ניתוח נתונים כדי להבין כיצד נעשה שימוש באתר. באפשרותך לקבל או לדחות עוגיות אלו.`,
      acceptAllBtn: `קבל הכל`,
      acceptNecessaryBtn: `דחה הכל`,
      showPreferencesBtn: `ניהול הגדרות`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">מדיניות פרטיות</a>`
    },
    preferencesModal: {
      title: `ניהול הגדרות העוגיות`,
      acceptAllBtn: `קבל הכל`,
      acceptNecessaryBtn: `דחה הכל`,
      savePreferencesBtn: `שמור את ההגדרות`,
      closeIconLabel: `סגור`,
      sections: [
        {
          title: `אפשרויות הפרטיות שלך`,
          description: `לוח בקרה זה מאפשר לך לבחור אילו עוגיות וכלים שאינם חיוניים אנו רשאים להשתמש בהם. לפרטים נוספים, עיין <a href="${privacyPolicyLink}" target="_blank" rel="noopener">במדיניות הפרטיות</a> שלנו.`
        },
        {
          title: `הכרחי בהחלט`,
          description: `עוגיות אלה חיוניות לתפקוד האתר - למשל, אבטחה, שמירת מצב התחברותך, שמירת הפעילות שלך באתר, זיכרון בחירת העוגיות שלך וצ'אט התמיכה. לא ניתן להשבית אותן.`,
          linkedCategory: `necessary`
        },
        {
          title: `ניתוח נתונים`,
          description: `Google Analytics מסייע לנו להבין כיצד נעשה שימוש באתר, כדי שנוכל לשפר אותו. לא נעשה שימוש בעוגיות פרסום או שיווק.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'hi': {
    consentModal: {
      title: `हम आपकी गोपनीयता का सम्मान करते हैं`,
      description: `हम आपको साइन इन बनाए रखने और सहायता चैट प्रदान करने के लिए आवश्यक कुकीज़ का उपयोग करते हैं। ये हमेशा चालू रहती हैं। आपकी अनुमति से, हम साइट के उपयोग को समझने के लिए विश्लेषिकी कुकीज़ का भी उपयोग करते हैं। आप इन्हें स्वीकार या अस्वीकार कर सकते हैं।`,
      acceptAllBtn: `सभी स्वीकार करें`,
      acceptNecessaryBtn: `सभी अस्वीकार करें`,
      showPreferencesBtn: `प्राथमिकताएँ प्रबंधित करें`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">गोपनीयता नीति</a>`
    },
    preferencesModal: {
      title: `कुकी प्राथमिकताएँ प्रबंधित करें`,
      acceptAllBtn: `सभी स्वीकार करें`,
      acceptNecessaryBtn: `सभी अस्वीकार करें`,
      savePreferencesBtn: `प्राथमिकताएँ सहेजें`,
      closeIconLabel: `बंद करें`,
      sections: [
        {
          title: `आपके गोपनीयता विकल्प`,
          description: `यह पैनल आपको यह चुनने की अनुमति देता है कि हम कौन-सी गैर-आवश्यक कुकीज़ और उपकरण उपयोग कर सकते हैं। अधिक जानकारी के लिए हमारी <a href="${privacyPolicyLink}" target="_blank" rel="noopener">गोपनीयता नीति</a> देखें।`
        },
        {
          title: `अत्यंत आवश्यक`,
          description: `ये कुकीज़ साइट के सुचारू रूप से काम करने के लिए आवश्यक हैं - उदाहरण के लिए सुरक्षा, आपको साइन इन बनाए रखना, आपका सत्र, आपकी कुकी पसंद को याद रखना, और सहायता चैट। इन्हें बंद नहीं किया जा सकता।`,
          linkedCategory: `necessary`
        },
        {
          title: `विश्लेषिकी`,
          description: `Google Analytics हमें यह समझने में मदद करता है कि साइट का उपयोग कैसे किया जाता है ताकि हम इसे बेहतर बना सकें। कोई विज्ञापन या मार्केटिंग कुकीज़ का उपयोग नहीं किया जाता है।`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'id': {
    consentModal: {
      title: `Kami menghargai privasi Anda`,
      description: `Kami menggunakan cookie penting untuk menjaga agar Anda tetap masuk dan menyediakan layanan obrolan dukungan. Cookie ini selalu aktif. Dengan izin Anda, kami juga menggunakan cookie analitik untuk memahami cara penggunaan situs ini. Anda dapat menerima atau menolak cookie tersebut.`,
      acceptAllBtn: `Terima semua`,
      acceptNecessaryBtn: `Tolak semua`,
      showPreferencesBtn: `Kelola preferensi`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Kebijakan Privasi</a>`
    },
    preferencesModal: {
      title: `Kelola preferensi cookie`,
      acceptAllBtn: `Terima semua`,
      acceptNecessaryBtn: `Tolak semua`,
      savePreferencesBtn: `Simpan preferensi`,
      closeIconLabel: `Tutup`,
      sections: [
        {
          title: `Pilihan privasi Anda`,
          description: `Panel ini memungkinkan Anda memilih cookie dan alat non-esensial mana saja yang mungkin kami gunakan. Lihat <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Kebijakan Privasi</a> kami untuk informasi lebih lanjut.`
        },
        {
          title: `Sangat diperlukan`,
          description: `Cookie-cookie ini diperlukan agar situs ini dapat berfungsi - misalnya, untuk keamanan, menjaga agar Anda tetap masuk, sesi Anda, mengingat pilihan cookie Anda, dan layanan obrolan dukungan. Cookie-cookie ini tidak dapat dinonaktifkan.`,
          linkedCategory: `necessary`
        },
        {
          title: `Analitik`,
          description: `Google Analytics membantu kami memahami bagaimana situs ini digunakan sehingga kami dapat memperbaikinya. Tidak ada cookie iklan atau pemasaran yang digunakan.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'it': {
    consentModal: {
      title: `Teniamo molto alla tua privacy`,
      description: `Utilizziamo i cookie essenziali per mantenere l'accesso e fornirti la chat di assistenza. Questi sono sempre attivi. Con il tuo consenso, utilizziamo anche cookie analitici per capire come viene utilizzato il sito. Puoi accettarli o rifiutarli.`,
      acceptAllBtn: `Accetta tutto`,
      acceptNecessaryBtn: `Rifiuta tutto`,
      showPreferencesBtn: `Gestisci le preferenze`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Informativa sulla privacy</a>`
    },
    preferencesModal: {
      title: `Gestisci le preferenze relative ai cookie`,
      acceptAllBtn: `Accetta tutto`,
      acceptNecessaryBtn: `Rifiuta tutto`,
      savePreferencesBtn: `Salva le preferenze`,
      closeIconLabel: `Chiudi`,
      sections: [
        {
          title: `Le tue preferenze in materia di privacy`,
          description: `Questo pannello ti permette di scegliere quali cookie e strumenti non essenziali possiamo utilizzare. Per ulteriori dettagli, consulta la nostra <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Informativa sulla privacy</a>.`
        },
        {
          title: `Strettamente necessari`,
          description: `Questi cookie sono necessari per il corretto funzionamento del sito - ad esempio, per la sicurezza, per mantenere l'accesso, per la sessione, per ricordare le tue preferenze relative ai cookie e per la chat di assistenza. Non è possibile disattivarli.`,
          linkedCategory: `necessary`
        },
        {
          title: `Analisi`,
          description: `Google Analytics ci aiuta a capire come viene utilizzato il sito, in modo da poterlo migliorare. Non vengono utilizzati cookie pubblicitari o di marketing.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'ja': {
    consentModal: {
      title: `当社はお客様のプライバシーを尊重いたします`,
      description: `当サイトでは、ログイン状態を維持し、サポートチャットを提供するために必須のCookieを使用しています。これらは常に有効になっています。また、お客様の許可を得た上で、サイトの利用状況を把握するために分析用Cookieも使用しています。これらのCookieについては、受け入れるか拒否するかを選択できます。`,
      acceptAllBtn: `すべて受け入れる`,
      acceptNecessaryBtn: `すべて拒否`,
      showPreferencesBtn: `設定の管理`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">プライバシーポリシー</a>`
    },
    preferencesModal: {
      title: `Cookieの設定を管理する`,
      acceptAllBtn: `すべて受け入れる`,
      acceptNecessaryBtn: `すべて拒否`,
      savePreferencesBtn: `設定を保存する`,
      closeIconLabel: `閉じる`,
      sections: [
        {
          title: `プライバシーに関する選択肢`,
          description: `このパネルでは、当社が使用する可能性のある非必須のCookieやツールを選択できます。詳細については、当社の<a href="${privacyPolicyLink}" target="_blank" rel="noopener">プライバシーポリシー</a>をご覧ください。`
        },
        {
          title: `必須`,
          description: `これらのCookieは、サイトの機能（セキュリティ、ログイン状態の維持、セッションの管理、Cookie設定の記憶、サポートチャットなど）に不可欠です。これらを無効にすることはできません。`,
          linkedCategory: `necessary`
        },
        {
          title: `分析`,
          description: `Google Analyticsを利用することで、当サイトの利用状況を把握し、改善に役立てています。広告やマーケティング目的のCookieは一切使用していません。`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'ko': {
    consentModal: {
      title: `당사는 귀하의 개인정보 보호를 중요하게 생각합니다`,
      description: `당사는 사용자의 로그인 상태를 유지하고 지원 채팅을 제공하기 위해 필수 쿠키를 사용합니다. 이 쿠키는 항상 활성화되어 있습니다. 또한 사용자의 동의가 있을 경우, 사이트 이용 현황을 파악하기 위해 분석용 쿠키도 사용합니다. 사용자는 이를 수락하거나 거부할 수 있습니다.`,
      acceptAllBtn: `모두 수락`,
      acceptNecessaryBtn: `모두 거부`,
      showPreferencesBtn: `쿠키 설정 관리`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">개인정보 처리방침</a>`
    },
    preferencesModal: {
      title: `쿠키 설정 관리`,
      acceptAllBtn: `모두 수락`,
      acceptNecessaryBtn: `모두 거부`,
      savePreferencesBtn: `설정 저장`,
      closeIconLabel: `닫기`,
      sections: [
        {
          title: `개인정보 처리 관련 선택 사항`,
          description: `이 패널을 통해 당사가 사용할 수 있는 비필수 쿠키 및 도구를 선택할 수 있습니다. 자세한 내용은 당사의 <a href="${privacyPolicyLink}" target="_blank" rel="noopener">개인정보 처리방침</a>을 참조하십시오.`
        },
        {
          title: `반드시 필요한 사항`,
          description: `이 쿠키들은 사이트가 정상적으로 작동하는 데 필수적입니다. 예를 들어, 보안, 로그인 상태 유지, 세션 관리, 쿠키 설정 기억, 지원 채팅 등이 이에 해당합니다. 이 쿠키들은 비활성화할 수 없습니다.`,
          linkedCategory: `necessary`
        },
        {
          title: `분석`,
          description: `Google Analytics는 사이트가 어떻게 이용되는지 파악하여 사이트를 개선하는 데 도움을 줍니다. 광고나 마케팅용 쿠키는 사용되지 않습니다.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'ms': {
    consentModal: {
      title: `Kami menghargai privasi anda`,
      description: `Kami menggunakan kuki penting untuk memastikan anda kekal log masuk dan menyediakan sembang sokongan. Kuki ini sentiasa diaktifkan. Dengan kebenaran anda, kami juga menggunakan kuki analitik untuk memahami cara laman ini digunakan. Anda boleh menerimanya atau menolaknya.`,
      acceptAllBtn: `Terima semua`,
      acceptNecessaryBtn: `Tolak semua`,
      showPreferencesBtn: `Urus keutamaan`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Dasar Privasi</a>`
    },
    preferencesModal: {
      title: `Urus keutamaan kuki`,
      acceptAllBtn: `Terima semua`,
      acceptNecessaryBtn: `Tolak semua`,
      savePreferencesBtn: `Simpan keutamaan`,
      closeIconLabel: `Tutup`,
      sections: [
        {
          title: `Pilihan privasi anda`,
          description: `Panel ini membolehkan anda memilih kuki dan alat bukan penting yang mungkin kami gunakan. Lihat <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Dasar Privasi</a> kami untuk maklumat lanjut.`
        },
        {
          title: `Sangat diperlukan`,
          description: `Kuki ini diperlukan untuk laman web berfungsi - contohnya keselamatan, memastikan anda kekal log masuk, sesi anda, mengingati pilihan kuki anda, dan sembang sokongan. Kuki ini tidak boleh dilumpuhkan.`,
          linkedCategory: `necessary`
        },
        {
          title: `Analitik`,
          description: `Google Analytics membantu kami memahami cara laman web ini digunakan supaya kami dapat mempertingkatkannya. Tiada kuki pengiklanan atau pemasaran digunakan.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'nl': {
    consentModal: {
      title: `Wij hechten veel waarde aan uw privacy`,
      description: `We gebruiken essentiële cookies om u ingelogd te houden en om ondersteuning via de chat te bieden. Deze staan altijd ingeschakeld. Met uw toestemming gebruiken we ook analytische cookies om inzicht te krijgen in hoe de website wordt gebruikt. U kunt deze accepteren of weigeren.`,
      acceptAllBtn: `Alles accepteren`,
      acceptNecessaryBtn: `Alles weigeren`,
      showPreferencesBtn: `Voorkeuren beheren`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Privacybeleid</a>`
    },
    preferencesModal: {
      title: `Cookievoorkeuren beheren`,
      acceptAllBtn: `Alles accepteren`,
      acceptNecessaryBtn: `Alles weigeren`,
      savePreferencesBtn: `Voorkeuren opslaan`,
      closeIconLabel: `Sluiten`,
      sections: [
        {
          title: `Uw privacykeuzes`,
          description: `Via dit venster kunt u kiezen welke niet-essentiële cookies en tools wij mogen gebruiken. Raadpleeg ons <a href="${privacyPolicyLink}" target="_blank" rel="noopener">privacybeleid</a> voor meer informatie.`
        },
        {
          title: `Absoluut noodzakelijk`,
          description: `Deze cookies zijn noodzakelijk voor de werking van de website - bijvoorbeeld voor de beveiliging, om u ingelogd te houden, voor uw sessie, om uw cookievoorkeuren te onthouden en voor de ondersteunings-chat. Ze kunnen niet worden uitgeschakeld.`,
          linkedCategory: `necessary`
        },
        {
          title: `Analytics`,
          description: `Google Analytics helpt ons inzicht te krijgen in hoe de website wordt gebruikt, zodat we deze kunnen verbeteren. Er worden geen advertentie- of marketingcookies gebruikt.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'pl': {
    consentModal: {
      title: `Szanujemy Twoją prywatność`,
      description: `Używamy niezbędnych plików cookie, aby utrzymać Twoje logowanie oraz zapewnić działanie czatu pomocy technicznej. Są one zawsze aktywne. Za Twoją zgodą korzystamy również z analitycznych plików cookie, aby lepiej zrozumieć, w jaki sposób korzystasz z naszej strony. Możesz je zaakceptować lub odrzucić.`,
      acceptAllBtn: `Zaakceptuj wszystko`,
      acceptNecessaryBtn: `Odrzuć wszystko`,
      showPreferencesBtn: `Zarządzaj ustawieniami`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Polityka prywatności</a>`
    },
    preferencesModal: {
      title: `Zarządzaj ustawieniami plików cookie`,
      acceptAllBtn: `Zaakceptuj wszystko`,
      acceptNecessaryBtn: `Odrzuć wszystko`,
      savePreferencesBtn: `Zapisz ustawienia`,
      closeIconLabel: `Zamknij`,
      sections: [
        {
          title: `Twoje opcje dotyczące prywatności`,
          description: `W tym panelu możesz wybrać, z których opcjonalnych plików cookie i narzędzi możemy korzystać. Więcej szczegółów znajdziesz w naszej <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Polityce prywatności</a>.`
        },
        {
          title: `Absolutnie niezbędne`,
          description: `Te pliki cookie są niezbędne do prawidłowego działania strony - służą na przykład do zapewnienia bezpieczeństwa, utrzymania logowania, sesji użytkownika, zapamiętania wybranych ustawień dotyczących plików cookie oraz obsługi czatu pomocy technicznej. Nie można ich wyłączyć.`,
          linkedCategory: `necessary`
        },
        {
          title: `Analizy`,
          description: `Google Analytics pomaga nam zrozumieć, w jaki sposób użytkownicy korzystają z tej strony, dzięki czemu możemy ją ulepszać. Nie stosujemy żadnych plików cookie związanych z reklamami ani marketingiem.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'pt': {
    consentModal: {
      title: `Respeitamos a sua privacidade`,
      description: `Utilizamos cookies essenciais para manter a sua sessão ativa e disponibilizar o chat de apoio. Estes estão sempre ativos. Com a sua autorização, também utilizamos cookies de análise para compreender como o site é utilizado. Pode aceitá-los ou rejeitá-los.`,
      acceptAllBtn: `Aceitar tudo`,
      acceptNecessaryBtn: `Rejeitar tudo`,
      showPreferencesBtn: `Gerir preferências`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Política de Privacidade</a>`
    },
    preferencesModal: {
      title: `Gerir as preferências de cookies`,
      acceptAllBtn: `Aceitar tudo`,
      acceptNecessaryBtn: `Rejeitar tudo`,
      savePreferencesBtn: `Guardar preferências`,
      closeIconLabel: `Fechar`,
      sections: [
        {
          title: `As suas opções de privacidade`,
          description: `Este painel permite-lhe escolher quais os cookies e ferramentas não essenciais que podemos utilizar. Consulte a nossa <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Política de Privacidade</a> para obter mais informações.`
        },
        {
          title: `Estritamente necessários`,
          description: `Estes cookies são necessários para o funcionamento do site - por exemplo, para questões de segurança, para manter a sua sessão ativa, para guardar a sua escolha relativa aos cookies e para o chat de apoio. Não é possível desativá-los.`,
          linkedCategory: `necessary`
        },
        {
          title: `Análise`,
          description: `O Google Analytics ajuda-nos a compreender como o site é utilizado, para que possamos melhorá-lo. Não são utilizados cookies de publicidade nem de marketing.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'ro': {
    consentModal: {
      title: `Punem preț pe confidențialitatea dumneavoastră`,
      description: `Folosim cookie-uri esențiale pentru a vă menține autentificat și pentru a vă oferi serviciul de chat pentru asistență. Acestea sunt întotdeauna active. Cu acordul dumneavoastră, folosim și cookie-uri de analiză pentru a înțelege modul în care este utilizat site-ul. Puteți să le acceptați sau să le refuzați.`,
      acceptAllBtn: `Acceptați totul`,
      acceptNecessaryBtn: `Respingeți totul`,
      showPreferencesBtn: `Gestionarea preferințelor`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Politica de confidențialitate</a>`
    },
    preferencesModal: {
      title: `Gestionați preferințele privind cookie-urile`,
      acceptAllBtn: `Acceptați totul`,
      acceptNecessaryBtn: `Respingeți totul`,
      savePreferencesBtn: `Salvați preferințele`,
      closeIconLabel: `Închideți`,
      sections: [
        {
          title: `Opțiunile dvs. privind confidențialitatea`,
          description: `Acest panou vă permite să alegeți ce cookie-uri și instrumente neesențiale putem utiliza. Pentru mai multe detalii, consultați <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Politica noastră de confidențialitate</a>.`
        },
        {
          title: `Absolut necesare`,
          description: `Aceste cookie-uri sunt necesare pentru funcționarea site-ului - de exemplu, pentru securitate, menținerea stării de conectare, sesiunea dvs., memorarea opțiunilor dvs. privind cookie-urile și chat-ul de asistență. Acestea nu pot fi dezactivate.`,
          linkedCategory: `necessary`
        },
        {
          title: `Analize`,
          description: `Google Analytics ne ajută să înțelegem modul în care este utilizat site-ul, pentru a-l putea îmbunătăți. Nu se utilizează cookie-uri de publicitate sau de marketing.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'ru': {
    consentModal: {
      title: `Мы уважаем вашу конфиденциальность`,
      description: `Мы используем обязательные файлы cookie для сохранения вашего входа в систему и обеспечения работы чата поддержки. Они всегда активны. С вашего разрешения мы также используем аналитические файлы cookie, чтобы понять, как используется сайт. Вы можете принять или отклонить их.`,
      acceptAllBtn: `Принять все`,
      acceptNecessaryBtn: `Отклонить все`,
      showPreferencesBtn: `Управление настройками`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Политика конфиденциальности</a>`
    },
    preferencesModal: {
      title: `Управление настройками файлов cookie`,
      acceptAllBtn: `Принять все`,
      acceptNecessaryBtn: `Отклонить все`,
      savePreferencesBtn: `Сохранить настройки`,
      closeIconLabel: `Закрыть`,
      sections: [
        {
          title: `Ваши настройки конфиденциальности`,
          description: `В этом окне вы можете выбрать, какие необязательные файлы cookie и инструменты мы можем использовать. Более подробную информацию см. в нашей <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Политике конфиденциальности</a>.`
        },
        {
          title: `Абсолютно необходимые`,
          description: `Эти файлы cookie необходимы для работы сайта - например, для обеспечения безопасности, поддержания входа в систему, сохранения сеанса, запоминания ваших настроек в отношении файлов cookie и работы чата поддержки. Их нельзя отключить.`,
          linkedCategory: `necessary`
        },
        {
          title: `Аналитика`,
          description: `Google Analytics помогает нам понять, как используется сайт, чтобы мы могли его улучшить. Рекламные и маркетинговые файлы cookie не используются.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'th': {
    consentModal: {
      title: `เราให้ความสำคัญต่อความเป็นส่วนตัวของคุณ`,
      description: `เราใช้คุกกี้ที่จำเป็นเพื่อรักษาสถานะการเข้าสู่ระบบของคุณและให้บริการแชทสนับสนุน คุกกี้เหล่านี้จะทำงานอยู่ตลอดเวลา นอกจากนี้ หากได้รับอนุญาตจากคุณ เรายังใช้คุกกี้วิเคราะห์เพื่อเข้าใจวิธีการใช้งานเว็บไซต์ คุณสามารถเลือกยอมรับหรือปฏิเสธคุกกี้เหล่านี้ได้`,
      acceptAllBtn: `ยอมรับทั้งหมด`,
      acceptNecessaryBtn: `ปฏิเสธทั้งหมด`,
      showPreferencesBtn: `จัดการการตั้งค่า`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">นโยบายความเป็นส่วนตัว</a>`
    },
    preferencesModal: {
      title: `จัดการการตั้งค่าคุกกี้`,
      acceptAllBtn: `ยอมรับทั้งหมด`,
      acceptNecessaryBtn: `ปฏิเสธทั้งหมด`,
      savePreferencesBtn: `บันทึกการตั้งค่า`,
      closeIconLabel: `ปิด`,
      sections: [
        {
          title: `ตัวเลือกด้านความเป็นส่วนตัวของคุณ`,
          description: `แผงควบคุมนี้ช่วยให้คุณสามารถเลือกได้ว่าเราอาจใช้คุกกี้และเครื่องมือใดที่ไม่จำเป็น ดู<a href="${privacyPolicyLink}" target="_blank" rel="noopener">นโยบายความเป็นส่วนตัว</a>ของเราเพื่อทราบรายละเอียดเพิ่มเติม`
        },
        {
          title: `จำเป็นอย่างยิ่ง`,
          description: `คุกกี้เหล่านี้จำเป็นเพื่อให้เว็บไซต์ทำงานได้ - เช่น ระบบความปลอดภัย การรักษาสถานะการเข้าสู่ระบบของคุณ การจัดการเซสชัน การจดจำการตั้งค่าคุกกี้ของคุณ และบริการแชทสนับสนุน คุณไม่สามารถปิดการใช้งานคุกกี้เหล่านี้ได้`,
          linkedCategory: `necessary`
        },
        {
          title: `การวิเคราะห์`,
          description: `Google Analytics ช่วยเราเข้าใจวิธีการใช้งานเว็บไซต์ เพื่อที่เราจะสามารถปรับปรุงเว็บไซต์ได้ ไม่มีการใช้คุกกี้เพื่อการโฆษณาหรือการตลาด`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'tr': {
    consentModal: {
      title: `Gizliliğinize büyük önem veriyoruz`,
      description: `Oturumunuzu açık tutmak ve destek sohbeti sunmak için temel çerezler kullanıyoruz. Bu çerezler her zaman etkindir. İzninizle, sitenin nasıl kullanıldığını anlamak amacıyla analiz çerezleri de kullanıyoruz. Bunları kabul edebilir veya reddedebilirsiniz.`,
      acceptAllBtn: `Hepsini kabul et`,
      acceptNecessaryBtn: `Hepsini reddet`,
      showPreferencesBtn: `Tercihleri yönet`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Gizlilik Politikası</a>`
    },
    preferencesModal: {
      title: `Çerez tercihlerini yönet`,
      acceptAllBtn: `Hepsini kabul et`,
      acceptNecessaryBtn: `Hepsini reddet`,
      savePreferencesBtn: `Tercihleri kaydet`,
      closeIconLabel: `Kapat`,
      sections: [
        {
          title: `Gizlilik tercihleriniz`,
          description: `Bu panel, hangi zorunlu olmayan çerezleri ve araçları kullanabileceğimizi seçmenize olanak tanır. Daha fazla bilgi için <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Gizlilik Politikamıza</a> göz atın.`
        },
        {
          title: `Kesinlikle gerekli`,
          description: `Bu çerezler, sitenin çalışması için gereklidir - örneğin, güvenlik, oturumunuzu açık tutma, çerez tercihlerinizi hatırlama ve destek sohbeti gibi. Bu çerezler devre dışı bırakılamaz.`,
          linkedCategory: `necessary`
        },
        {
          title: `Analitik`,
          description: `Google Analytics, sitenin nasıl kullanıldığını anlamamıza yardımcı olur; böylece siteyi iyileştirebiliriz. Herhangi bir reklam veya pazarlama çerezi kullanılmamaktadır.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'ur': {
    consentModal: {
      title: `ہم آپ کی رازداری کو قدر کی نگاہ سے دیکھتے ہیں`,
      description: `ہم ضروری کوکیز استعمال کرتے ہیں تاکہ آپ سائن ان رہیں اور سپورٹ چیٹ فراہم کی جا سکے۔ یہ ہمیشہ فعال رہتی ہیں۔ آپ کی اجازت سے، ہم تجزیاتی کوکیز بھی استعمال کرتے ہیں تاکہ یہ سمجھ سکیں کہ سائٹ کو کیسے استعمال کیا جاتا ہے۔ آپ انہیں قبول یا مسترد کر سکتے ہیں۔`,
      acceptAllBtn: `سب قبول کریں`,
      acceptNecessaryBtn: `سب مسترد کریں`,
      showPreferencesBtn: `ترجیحات کا انتظام کریں`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">رازداری کی پالیسی</a>`
    },
    preferencesModal: {
      title: `کوکی ترجیحات کا انتظام کریں`,
      acceptAllBtn: `سب قبول کریں`,
      acceptNecessaryBtn: `سب مسترد کریں`,
      savePreferencesBtn: `ترجیحات محفوظ کریں`,
      closeIconLabel: `بند کریں`,
      sections: [
        {
          title: `آپ کے رازداری کے انتخاب`,
          description: `یہ پینل آپ کو یہ منتخب کرنے کی اجازت دیتا ہے کہ ہم کون سی غیر ضروری کوکیز اور ٹولز استعمال کر سکتے ہیں۔ مزید تفصیلات کے لیے ہماری <a href="${privacyPolicyLink}" target="_blank" rel="noopener">رازداری کی پالیسی</a> دیکھیں۔`
        },
        {
          title: `انتہائی ضروری`,
          description: `یہ کوکیز سائٹ کے کام کرنے کے لیے ضروری ہیں - مثال کے طور پر سیکیورٹی، آپ کو سائن ان رکھنے، آپ کے سیشن کو برقرار رکھنے، آپ کی کوکی ترجیح کو یاد رکھنے، اور سپورٹ چیٹ کے لیے۔ انہیں بند نہیں کیا جا سکتا۔`,
          linkedCategory: `necessary`
        },
        {
          title: `تجزیات`,
          description: `Google Analytics ہمیں یہ سمجھنے میں مدد دیتا ہے کہ سائٹ کا استعمال کیسے ہوتا ہے تاکہ ہم اسے بہتر بنا سکیں۔ کوئی اشتہاری یا مارکیٹنگ کوکیز استعمال نہیں کی جاتیں۔`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'vi': {
    consentModal: {
      title: `Chúng tôi luôn coi trọng quyền riêng tư của bạn`,
      description: `Chúng tôi sử dụng các cookie thiết yếu để duy trì trạng thái đăng nhập của bạn và cung cấp tính năng trò chuyện hỗ trợ. Các cookie này luôn được kích hoạt. Với sự đồng ý của bạn, chúng tôi cũng sử dụng các cookie phân tích để tìm hiểu cách thức sử dụng trang web. Bạn có thể chấp nhận hoặc từ chối các cookie này.`,
      acceptAllBtn: `Chấp nhận tất cả`,
      acceptNecessaryBtn: `Từ chối tất cả`,
      showPreferencesBtn: `Quản lý cài đặt`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">Chính sách bảo mật</a>`
    },
    preferencesModal: {
      title: `Quản lý cài đặt cookie`,
      acceptAllBtn: `Chấp nhận tất cả`,
      acceptNecessaryBtn: `Từ chối tất cả`,
      savePreferencesBtn: `Lưu cài đặt`,
      closeIconLabel: `Đóng`,
      sections: [
        {
          title: `Các tùy chọn về quyền riêng tư của bạn`,
          description: `Bảng điều khiển này cho phép bạn chọn các cookie và công cụ không thiết yếu mà chúng tôi có thể sử dụng. Vui lòng tham khảo <a href="${privacyPolicyLink}" target="_blank" rel="noopener">Chính sách bảo mật</a> của chúng tôi để biết thêm chi tiết.`
        },
        {
          title: `Hoàn toàn cần thiết`,
          description: `Các cookie này là cần thiết để trang web hoạt động - ví dụ như đảm bảo an ninh, duy trì trạng thái đăng nhập của bạn, duy trì phiên làm việc của bạn, ghi nhớ lựa chọn về cookie của bạn và cung cấp tính năng trò chuyện hỗ trợ. Bạn không thể tắt các cookie này.`,
          linkedCategory: `necessary`
        },
        {
          title: `Phân tích`,
          description: `Google Analytics giúp chúng tôi hiểu cách người dùng sử dụng trang web để từ đó có thể cải thiện nó. Chúng tôi không sử dụng bất kỳ cookie quảng cáo hay tiếp thị nào.`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'zh-CN': {
    consentModal: {
      title: `我们重视您的隐私`,
      description: `我们使用必要 Cookie 来保持您的登录状态并提供在线客服支持。这些 Cookie 始终处于启用状态。在征得您同意的情况下，我们还会使用分析类 Cookie 来了解网站的使用情况。您可以选择接受或拒绝这些 Cookie。`,
      acceptAllBtn: `全部接受`,
      acceptNecessaryBtn: `全部拒绝`,
      showPreferencesBtn: `管理偏好设置`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">隐私政策</a>`
    },
    preferencesModal: {
      title: `管理 Cookie 偏好设置`,
      acceptAllBtn: `全部接受`,
      acceptNecessaryBtn: `全部拒绝`,
      savePreferencesBtn: `保存偏好设置`,
      closeIconLabel: `关闭`,
      sections: [
        {
          title: `您的隐私选项`,
          description: `通过此面板，您可以选择我们可能使用的哪些非必要 Cookie 和工具。更多详情请参阅我们的《<a href="${privacyPolicyLink}" target="_blank" rel="noopener">隐私政策</a>》。`
        },
        {
          title: `绝对必要`,
          description: `这些 Cookie 是网站正常运行所必需的，例如：用于保障安全、保持您的登录状态、维护您的会话、记住您的 Cookie 设置以及提供在线客服支持。这些 Cookie 无法关闭。`,
          linkedCategory: `necessary`
        },
        {
          title: `分析`,
          description: `Google Analytics 帮助我们了解网站的使用情况，以便我们对其进行改进。本网站不使用任何广告或营销 Cookie。`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
  'zh-TW': {
    consentModal: {
      title: `我們重視您的隱私`,
      description: `我們使用必要的 Cookie 來維持您的登入狀態並提供支援聊天服務。這些 Cookie 始終處於啟用狀態。在獲得您的許可後，我們也會使用分析型 Cookie 來了解網站的使用情況。您可以選擇接受或拒絕這些 Cookie。`,
      acceptAllBtn: `全數接受`,
      acceptNecessaryBtn: `全數拒絕`,
      showPreferencesBtn: `管理偏好設定`,
      footer: `<a href="${privacyPolicyLink}" target="_blank" rel="noopener">隱私權政策</a>`
    },
    preferencesModal: {
      title: `管理 Cookie 偏好設定`,
      acceptAllBtn: `全數接受`,
      acceptNecessaryBtn: `全數拒絕`,
      savePreferencesBtn: `儲存偏好設定`,
      closeIconLabel: `關閉`,
      sections: [
        {
          title: `您的隱私權設定`,
          description: `透過此面板，您可以選擇我們可能使用的非必要 Cookie 和工具。更多詳細資訊請參閱我們的《<a href="${privacyPolicyLink}" target="_blank" rel="noopener">隱私權政策</a>》。`
        },
        {
          title: `絕對必要`,
          description: `這些 Cookie 是網站正常運作所必需的，例如：用於安全防護、維持您的登入狀態、管理您的工作階段、記住您的 Cookie 設定，以及提供線上支援聊天功能。這些 Cookie 無法關閉。`,
          linkedCategory: `necessary`
        },
        {
          title: `分析`,
          description: `Google Analytics 協助我們了解網站的使用情況，以便我們加以改善。本網站未使用任何廣告或行銷 Cookie。`,
          linkedCategory: `analytics`
        }
      ]
    }
  },
};

export const CONSENT_DEFAULT_LANGUAGE = 'en';
